import React, { useMemo } from "react";
import { displayLabel } from "../../utils/format";

const BookingWindowPanel = ({ data, loading }) => {
  const { overallAvg, overallMedian } = useMemo(() => {
    if (!data?.length) return { overallAvg: 0, overallMedian: 0 };
    let totalDays = 0;
    let totalSessions = 0;
    let totalMedianDays = 0;
    for (const row of data) {
      totalDays += row.avg_lead_days * row.sessions;
      totalMedianDays += row.median_lead_days * row.sessions;
      totalSessions += row.sessions;
    }
    return {
      overallAvg: totalSessions > 0 ? Math.round(totalDays / totalSessions * 10) / 10 : 0,
      overallMedian: totalSessions > 0 ? Math.round(totalMedianDays / totalSessions) : 0,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-12 bg-gray-200 dark:bg-gray-800 rounded w-1/3" />
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-6 bg-gray-200 dark:bg-gray-800 rounded" style={{ width: `${80 - i * 10}%` }} />
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
        No booking window data for this period.
      </div>
    );
  }

  const maxDays = Math.max(...data.map((d) => d.avg_lead_days), 1);

  return (
    <div>
      {/* KPI callout */}
      <div className="flex items-end gap-6 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
        <div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{overallAvg}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Avg days before check-in</div>
        </div>
        <div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{overallMedian}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Median days</div>
        </div>
      </div>

      {/* Horizontal bar chart */}
      <div className="space-y-2.5">
        {data.map((row, idx) => {
          const siteLabel = displayLabel(row.site_type);
          return (
          <div key={row.site_type} className={`max-[499px]:rounded-md max-[499px]:px-2 max-[499px]:py-2 ${idx % 2 === 0 ? "max-[499px]:bg-gray-50 dark:max-[499px]:bg-gray-800/40" : ""}`}>
            <div className="flex items-center justify-between mb-1 max-[499px]:flex-col max-[499px]:items-start max-[499px]:gap-0.5">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[140px] max-[499px]:max-w-full" title={siteLabel}>
                {siteLabel}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0 ml-2 max-[499px]:ml-0">
                {Math.round(row.avg_lead_days)} days avg · {row.median_lead_days} median
              </span>
            </div>
            <div
              className="flex items-center gap-2"
              title={`${siteLabel}\n${row.sessions} sessions · ${row.bookings} booked\n${row.avg_lead_days} days avg lead time\n${row.median_lead_days} days median`}
            >
              <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-4 relative overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#32B1FF]/30 transition-all"
                  style={{ width: `${(row.avg_lead_days / maxDays) * 100}%` }}
                />
                <div
                  className="absolute top-0 left-0 h-full rounded-full bg-[#32B1FF] transition-all"
                  style={{ width: `${(row.bookings / Math.max(row.sessions, 1)) * (row.avg_lead_days / maxDays) * 100}%` }}
                />
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400 w-10 text-right shrink-0">{row.sessions}</span>
            </div>
          </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-[#32B1FF]/30" /><span className="text-sm text-gray-500 dark:text-gray-400">Sessions</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-[#32B1FF]" /><span className="text-sm text-gray-500 dark:text-gray-400">Bookings</span></div>
      </div>
    </div>
  );
};

export default BookingWindowPanel;
